import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Paper, Button, Divider, TextField, IconButton, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, Box
} from "@mui/material";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Add } from "@mui/icons-material";
import * as ETVConstants from "../ETVConstants";
import LoadingDialog from "../tools/LoadingDialog";

const grid = 1;

/** Dense item style (smaller height, tighter paddings) */
const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  padding: "4px 6px",
  margin: `0 0 ${grid}px 0`,
  minHeight: 36,
  borderRadius: 6,
  border: "1px solid #e0e0e0",
  background: isDragging ? "#f0fff0" : "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  ...draggableStyle,
});

/** Compact droppable area with dashed highlight */
const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? "#f6fff6" : "transparent",
  border: isDraggingOver ? "1px dashed #a5d6a7" : "1px dashed transparent",
  transition: "border-color 120ms ease",
  padding: 4,
  width: 220,
  margin: 6,
  minHeight: 80,
  borderRadius: 8,
});

class GroupMgmt extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      groups: [],         // array of arrays (index 0 = "all devices")
      groupNames: [],     // parallel to groups
      group_id: [],       // ["ALL", <db ids...>] parallel to groups
      edit_id: -1,
      isLoaded: false,
      error: null,
      // UX
      filterText: "",
      snackbar: { open: false, message: "", severity: "success" },
      deleteConfirm: { open: false, index: null },
    };
  }

  componentDidMount() {
    this.loadGroups();
  }

  /* ---------------------- Stable IDs ---------------------- */
  getDroppableId = (groupIndex) => {
    const gid = this.state.group_id[groupIndex];
    return gid === "ALL" ? "group-ALL" : `group-${String(gid)}`;
  };

  getIndexFromDroppableId = (droppableId) => {
    if (droppableId === "group-ALL") return 0;
    const gidStr = droppableId.replace("group-", "");
    const gid = isNaN(+gidStr) ? gidStr : +gidStr;
    return this.state.group_id.findIndex((g) => g === gid);
  };

  /* ---------------------- Transform server data ---------------------- */
  getGroupNames = (groups) => {
    const res = ["all devices"];
    for (let i = 0; i < groups.length; i++) res[i + 1] = groups[i].group_name;
    return res;
  };

  getGroupIDs = (groups) => {
    const res = ["ALL"];
    for (let i = 0; i < groups.length; i++) res[i + 1] = groups[i].group_id;
    return res;
  };

  createGroupArray = (groupInfo) => {
    const res = [];
    const deviceIDs = groupInfo.device_ids || [];
    const deviceNames = groupInfo.device_names || [];
    for (let k = 0; k < deviceIDs.length; k++) {
      res.push({ device_id: String(deviceIDs[k]), device_name: deviceNames[k] });
    }
    return res;
  };

  createGroups = (groups) => {
    const res = [[]]; // 0 = "all devices"
    for (let i = 0; i < groups.length; i++) res[i + 1] = this.createGroupArray(groups[i]);
    return res;
  };

  /* ---------------------- Fetchers ---------------------- */
  loadGroups = () => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url = `${ETVConstants.getServerBase()}/group_mgmt?action=list&user_id=${this.props.user_id}`;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          const groups = this.createGroups(result.list || []);
          const groupNames = this.getGroupNames(result.list || []);
          const groupIDs = this.getGroupIDs(result.list || []);
          this.setState(
            { groups, groupNames, group_id: groupIDs },
            this.loadDevices
          );
        },
        (error) => this.setState({ error, isLoaded: true })
      );
  };

  loadDevices = () => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url = `${ETVConstants.getServerBase()}/device_mgmt?action=list&owner_id=${this.props.user_id}`;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          const devices = Array.isArray(result.list) ? result.list : [];
          const available = this.filterOutDevicesFromGroups(devices);
          const group0Devices = this.createGroupArrayFromDevices(available);

          const newGroups = [...this.state.groups];
          newGroups[0] = group0Devices;

          const newGroupNames = [...this.state.groupNames];
          newGroupNames[0] = "all devices";

          this.setState({
            groups: newGroups,
            groupNames: newGroupNames,
            isLoaded: true,
            error: null,
          });
        },
        (error) => this.setState({ isLoaded: true, error })
      );
  };

  /* ---------------------- Device/group utils ---------------------- */
  createGroupArrayFromDevices = (devices) =>
    devices.map((d) => ({ device_id: String(d.id), device_name: d.device_name }));

  deviceExistsInGroup = (deviceID, groupIndex) => {
    const list = this.state.groups[groupIndex] || [];
    return list.some((x) => String(x.device_id) === String(deviceID));
  };

  deviceExistsInAnyGroup = (deviceID) => {
    for (let i = 1; i < this.state.groups.length; i++) {
      if (this.deviceExistsInGroup(deviceID, i)) return true;
    }
    return false;
  };

  filterOutDevicesFromGroups = (allDevices) =>
    allDevices.filter((d) => !this.deviceExistsInAnyGroup(d.id));

  /** Keep only one of each device_id */
  dedupeList = (list) => {
    const seen = new Set();
    return list.filter((it) => {
      const id = String(it.device_id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  /** Reorder within the same group without creating duplicates */
  reorderWithinGroup = (groupIndex, startIndex, endIndex) => {
    const groups = this.state.groups.map((g) => [...g]);
    const list = groups[groupIndex];
    const [moved] = list.splice(startIndex, 1);
    list.splice(endIndex, 0, moved);
    groups[groupIndex] = this.dedupeList(list);
    this.setState({ groups });
  };

  /* ---------------------- Server mutations ---------------------- */
  addToGroupDB = (deviceID, groupID) => {
    const url = `${ETVConstants.getServerBase()}/group_mgmt?action=add_or_update_group&device_id=${deviceID}&group_id=${groupID}`;
    ETVConstants.sendCMDToServer(url);
  };

  removeDeviceFromGroupInDB = (groupID, deviceID) => {
    const url = `${ETVConstants.getServerBase()}/group_mgmt?action=remove_device_from_group&device_id=${deviceID}&group_id=${groupID}`;
    ETVConstants.sendCMDToServer(url);
  };

  addGroupToDB = (groupIndex, groupName) => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url = `${ETVConstants.getServerBase()}/group_mgmt?action=add_group&user_id=${this.props.user_id}&group_name=${encodeURIComponent(
      groupName
    )}`;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          const newId = result?.data;
          const groupIDs = [...this.state.group_id];
          groupIDs[groupIndex] = newId;
          this.setState({ group_id: groupIDs });
        },
        () => {}
      );
  };

  renameGroupInDB = (groupID, groupName) => {
    const url = `${ETVConstants.getServerBase()}/group_mgmt?action=update_group_name&group_id=${groupID}&group_name=${encodeURIComponent(
      groupName
    )}`;
    ETVConstants.sendCMDToServer(url);
  };

  deleteGroupInDB = (groupID) => {
    const url = `${ETVConstants.getServerBase()}/group_mgmt?action=delete_group&group_id=${groupID}`;
    ETVConstants.sendCMDToServer(url);
  };

  /* ---------------------- UI actions ---------------------- */
  switchGroup = (item, itemIndex, fromGroupIndex, toGroupIndex) => {
    const groups = this.state.groups.map((g) => [...g]);

    // remove from source
    groups[fromGroupIndex].splice(itemIndex, 1);

    // add to destination only if not already present
    const exists = groups[toGroupIndex].some(
      (x) => String(x.device_id) === String(item.device_id)
    );
    if (!exists) {
      groups[toGroupIndex].push(item);
    }

    // safety dedupe
    groups[toGroupIndex] = this.dedupeList(groups[toGroupIndex]);

    this.setState({ groups });

    const deviceID = item.device_id;
    const toGroupID = this.state.group_id[toGroupIndex];
    const fromGroupID = this.state.group_id[fromGroupIndex];

    if (toGroupIndex === 0) {
      // moved back to "all devices"
      this.removeDeviceFromGroupInDB(fromGroupID, deviceID);
      this.openSnack("Moved back to 'all devices'.", "info");
    } else if (!exists) {
      // only call DB add if it wasn't already there
      this.addToGroupDB(deviceID, toGroupID);
      this.openSnack(`Added to ${this.state.groupNames[toGroupIndex]}.`, "success");
    }
  };

  confirmDeleteGroup = (groupIndex) =>
    this.setState({ deleteConfirm: { open: true, index: groupIndex } });

  cancelDeleteGroup = () =>
    this.setState({ deleteConfirm: { open: false, index: null } });

  deleteGroup = () => {
    const groupIndex = this.state.deleteConfirm.index;
    if (groupIndex === 0 || groupIndex == null) return;
    const groupID = this.state.group_id[groupIndex];

    const newGroups = this.state.groups.map((g) => [...g]);
    const elemsToStore = newGroups[groupIndex] || [];
    newGroups[0].push(...elemsToStore);
    newGroups[0].sort(ETVConstants.compareDevice);
    newGroups.splice(groupIndex, 1);

    const groupNames = [...this.state.groupNames];
    const deletedName = groupNames[groupIndex];
    groupNames.splice(groupIndex, 1);

    const groupIDs = [...this.state.group_id];
    groupIDs.splice(groupIndex, 1);

    this.setState(
      { groupNames, groups: newGroups, group_id: groupIDs, edit_id: -1, deleteConfirm: { open: false, index: null } },
      () => {
        this.deleteGroupInDB(groupID);
        this.openSnack(`Deleted group "${deletedName}". Devices moved to 'all devices'.`, "warning");
      }
    );
  };

  addNewGroup = () => {
    const groupName = this.createNewGroupName();
    const tempId = `TMP-${Date.now()}`;
    this.setState(
      (prev) => ({
        groupNames: [...prev.groupNames, groupName],
        groups: [...prev.groups, []],
        group_id: [...prev.group_id, tempId],
      }),
      () => {
        const index = this.state.groups.length - 1;
        this.addGroupToDB(index, groupName);
        this.openSnack(`Created "${groupName}".`, "success");
      }
    );
  };

  editGroupName = (groupIndex) => this.setState({ edit_id: groupIndex });

  updateGroupName = (groupIndex, name) => {
    const groupNames = [...this.state.groupNames];
    groupNames[groupIndex] = name;
    this.setState({ groupNames });
  };

  submitGroupName = () => {
    const groupIndex = this.state.edit_id;
    if (groupIndex < 0) return;
    const groupName = this.state.groupNames[groupIndex];
    const groupID = this.state.group_id[groupIndex];
    if (groupID !== "ALL" && !String(groupID).startsWith("TMP-")) {
      this.renameGroupInDB(groupID, groupName);
    }
    this.setState({ edit_id: -1 });
    this.openSnack("Group name updated.", "success");
  };

  createNewGroupName = () => {
    let i = 1;
    let candidate = `Group ${i}`;
    while (this.state.groupNames.indexOf(candidate) >= 0) {
      i++;
      candidate = `Group ${i}`;
    }
    return candidate;
  };

  /* ---------------------- DnD ---------------------- */
  onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const sInd = this.getIndexFromDroppableId(source.droppableId);
    const dInd = this.getIndexFromDroppableId(destination.droppableId);
    if (sInd === -1 || dInd === -1) return;

    // SAME GROUP → reorder only (no duplicates)
    if (sInd === dInd) {
      if (source.index !== destination.index) {
        this.reorderWithinGroup(sInd, source.index, destination.index);
      }
      return;
    }

    // DIFFERENT GROUP → move between groups with dedupe/guards
    this.switchElement(sInd, source.index, dInd);
  };

  switchElement = (fromGroupIndex, elementIndex, toGroupIndex) => {
    const elem = this.state.groups[fromGroupIndex][elementIndex];
    if (!elem) return;
    this.switchGroup(elem, elementIndex, fromGroupIndex, toGroupIndex);
  };

  /* ---------------------- UX helpers ---------------------- */
  openSnack = (message, severity = "success") =>
    this.setState({ snackbar: { open: true, message, severity } });

  closeSnack = () =>
    this.setState({ snackbar: { ...this.state.snackbar, open: false } });

  /* ---------------------- Render helpers ---------------------- */
  renderDeviceRow = (item, index, groupIndex) => {
    return (
      <Draggable key={String(item.device_id)} draggableId={String(item.device_id)} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <Box
                {...provided.dragHandleProps}
                sx={{ display: "flex", alignItems: "center", px: 0.25, cursor: "grab" }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
              <Typography variant="caption" noWrap title={item.device_name}>
                {item.device_name}
              </Typography>
            </Box>

            {groupIndex !== 0 ? (
              <IconButton
                color="primary"
                size="small"
                onClick={() => this.switchElement(groupIndex, index, 0)}
              >
                <Typography variant="caption">[x]</Typography>
              </IconButton>
            ) : (
              <span />
            )}
          </div>
        )}
      </Draggable>
    );
  };

  renderGroupCard = (groupElements, groupIndex) => {
    const dId = this.getDroppableId(groupIndex);
    const isEditing = this.state.edit_id === groupIndex;
    const count = groupElements.length;

    const filteredElements =
      groupIndex === 0 && this.state.filterText.trim()
        ? groupElements.filter((it) =>
            (it.device_name || "").toLowerCase().includes(this.state.filterText.trim().toLowerCase())
          )
        : groupElements;

    return (
      <Paper key={dId} elevation={1} sx={{ m: 0.75, p: 1, width: 240, borderRadius: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
          {/* Fixed-width count block to guarantee space */}
          <Box
            sx={{
              width: 56,
              minWidth: 56,
              height: 36,
              borderRadius: 1.5,
              bgcolor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {count}
            </Typography>
          </Box>

          {/* Title/editor */}
          <Box sx={{ flexGrow: 1, minWidth: 0, mr: 0.5 }}>
            {!isEditing ? (
              <Typography
                id={`group_${groupIndex}`}
                variant="body2"
                noWrap
                sx={{ fontWeight: 600 }}
                title={this.state.groupNames[groupIndex]}
              >
                {this.state.groupNames[groupIndex]}
              </Typography>
            ) : (
              <TextField
                id={`group_${groupIndex}`}
                onChange={(e) => this.updateGroupName(groupIndex, e.target.value)}
                size="small"
                variant="outlined"
                value={this.state.groupNames[groupIndex] || ""}
                sx={{ width: "100%" }}
              />
            )}
          </Box>

          {/* Actions */}
          {groupIndex !== 0 ? (
            <Stack direction="row" spacing={0.25}>
              {isEditing ? (
                <IconButton size="small" color="primary" onClick={this.submitGroupName}>
                  <SaveIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton size="small" color="primary" onClick={() => this.editGroupName(groupIndex)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" color="error" onClick={() => this.confirmDeleteGroup(groupIndex)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ) : (
            <span />
          )}
        </Stack>

        {groupIndex === 0 && (
          <Box sx={{ mb: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search devices…"
              value={this.state.filterText}
              onChange={(e) => this.setState({ filterText: e.target.value })}
              InputProps={{ sx: { height: 34, fontSize: 13, px: 1 } }}
            />
          </Box>
        )}

        <Divider sx={{ mb: 0.5 }} />

        <Droppable droppableId={dId}>
          {(provided, snapshot) => (
            <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)} {...provided.droppableProps}>
              {filteredElements.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#9e9e9e", p: 1 }}>
                  {groupIndex === 0
                    ? this.state.filterText
                      ? "No matching devices."
                      : "No devices available."
                    : "No devices in this group yet."}
                </Typography>
              ) : (
                filteredElements.map((item, index) => this.renderDeviceRow(item, index, groupIndex))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </Paper>
    );
  };

  /* ---------------------- Render ---------------------- */
  render() {
    if (!this.state.isLoaded) {
      return <LoadingDialog open={!this.state.isLoaded} message={ETVConstants.trans("loading")} />;
    }

    return (
      <Box sx={{width:'calc(100% - 32px)',maxWidth:1500,m:'32px 0',ml:2,p:{xs:2,md:3.5},boxSizing:'border-box',border:'1px solid',borderColor:'divider',borderRadius:4,boxShadow:'0 10px 30px rgba(31, 41, 55, 0.08)'}}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Screen Groups
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" color="primary" size="small" startIcon={<Add />} onClick={this.addNewGroup}>
            Add
          </Button>
        </Stack>
		<Typography variant="body2" color="text.secondary" sx={{mb:3}}>Organize screens into groups by dragging devices between the columns.</Typography>

        <DragDropContext onDragEnd={this.onDragEnd}>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {this.state.groups.map((groupElements, groupIndex) => this.renderGroupCard(groupElements, groupIndex))}
          </Box>
        </DragDropContext>

        {/* Delete confirm */}
        <Dialog open={this.state.deleteConfirm.open} onClose={this.cancelDeleteGroup}>
          <DialogTitle>Delete this group?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Devices will be moved back to <b>all devices</b>. This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.cancelDeleteGroup}>Cancel</Button>
            <Button onClick={this.deleteGroup} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={this.state.snackbar.open}
          autoHideDuration={2500}
          onClose={this.closeSnack}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={this.state.snackbar.severity} onClose={this.closeSnack} sx={{ width: "100%" }}>
            {this.state.snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default GroupMgmt;
